import { Fragment, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, CheckSquare, MinusSquare, Square } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/admin/states/EmptyState";
import { ErrorState } from "@/components/admin/states/ErrorState";

const HIDE = { sm: "hidden sm:table-cell", md: "hidden md:table-cell", lg: "hidden lg:table-cell", xl: "hidden xl:table-cell" };

const compare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "nb", { numeric: true, sensitivity: "base" });
};

/**
 * Dense, readable admin table with a mobile “row card” fallback, sortable columns and a selected-row state.
 *
 * columns: [{ key, header, cell(row), className, headClassName, hide: 'sm'|'md'|'lg', align: 'right',
 *             primary (title on mobile), meta (meta line on mobile), status (right of title on mobile),
 *             actions (rendered under the card on mobile), leading (thumbnail), srOnly (visually hidden header),
 *             sortable, sortValue(row) }]
 * defaultSort: { key, dir: 'asc'|'desc' } – uncontrolled sorting state.
 * selectedKey: rowKey of the row currently open in a detail panel (gets a quiet highlight).
 * selection: { selected: string[], onToggle(key), onToggleAll(keys) } – adds a checkbox column for bulk actions.
 * separatorBefore(row, index, rows): return a label to draw a thin labelled rule above that row (e.g. «I dag»).
 */
export const DataTable = ({
  columns = [], rows = [], rowKey = (r) => r.id, onRowClick, rowTestId, rowClassName,
  loading = false, loadingRows = 6, error, onRetry, empty, emptyState,
  testId = "admin-data-table", className, dense = false, caption, mobileBreakpoint = "md",
  defaultSort = null, selectedKey = null, selection = null, separatorBefore = null,
}) => {
  const [sort, setSort] = useState(defaultSort);
  const primary = columns.find((c) => c.primary) || columns.find((c) => !c.leading && !c.actions) || columns[0];
  const leading = columns.find((c) => c.leading);
  const status = columns.find((c) => c.status);
  const actions = columns.find((c) => c.actions);
  const metas = columns.filter((c) => c.meta);
  const clickable = !!onRowClick;
  const cellPad = dense ? "px-3 py-1.5 sm:px-4" : "px-3 py-2 sm:px-4";
  const tableBp = mobileBreakpoint === "sm" ? "hidden sm:block" : mobileBreakpoint === "lg" ? "hidden lg:block" : "hidden md:block";
  const listBp = mobileBreakpoint === "sm" ? "sm:hidden" : mobileBreakpoint === "lg" ? "lg:hidden" : "md:hidden";

  const sorted = useMemo(() => {
    if (!sort?.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => compare(col.sortValue(a), col.sortValue(b)) * dir);
  }, [rows, sort, columns]);

  const toggleSort = (c) => setSort((s) => (s?.key === c.key ? (s.dir === "asc" ? { key: c.key, dir: "desc" } : null) : { key: c.key, dir: "asc" }));

  const selSet = useMemo(() => new Set(selection?.selected || []), [selection]);
  const allKeys = useMemo(() => sorted.map(rowKey), [sorted, rowKey]);
  const allSelected = selection && allKeys.length > 0 && allKeys.every((k) => selSet.has(k));
  const someSelected = selection && !allSelected && allKeys.some((k) => selSet.has(k));
  const SelectBox = ({ checked, indeterminate, onChange, label, testId: tid }) => (
    <button type="button" role="checkbox" aria-checked={indeterminate ? "mixed" : checked} aria-label={label} onClick={(e) => { e.stopPropagation(); onChange(); }} className={cn("flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground", (checked || indeterminate) && "text-foreground")} data-no-row-click data-testid={tid}>
      {checked ? <CheckSquare className="h-4 w-4" /> : indeterminate ? <MinusSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
    </button>
  );

  const keyHandler = (row) => (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); }
  };

  const headCell = (c) => {
    const sortable = !!c.sortable && !!c.sortValue;
    const active = sort?.key === c.key;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    const label = c.srOnly ? <span className="sr-only">{c.header}</span> : c.header;
    return (
      <TableHead key={c.key} scope="col" aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined} className={cn("h-8 whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground", cellPad, c.hide && HIDE[c.hide], c.align === "right" && "text-right", c.headClassName)} style={c.width ? { width: c.width } : undefined}>
        {sortable ? (
          <button type="button" onClick={() => toggleSort(c)} className={cn("group/sort -mx-1.5 inline-flex h-7 items-center gap-1 rounded-md px-1.5 uppercase tracking-[0.12em] transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground", active && "text-foreground")} data-testid={`${testId}-sort-${c.key}`}>
            {label}<Icon className={cn("h-3 w-3 transition-opacity", active ? "opacity-100" : "opacity-0 group-hover/sort:opacity-60")} aria-hidden="true" />
          </button>
        ) : label}
      </TableHead>
    );
  };

  let body;
  if (error) {
    body = <div className="p-4"><ErrorState title="Kunne ikkje hente data" text={typeof error === "string" ? error : error?.message} onRetry={onRetry} /></div>;
  } else if (loading) {
    body = (
      <>
        <div className={tableBp}>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{columns.map((c) => <TableHead key={c.key} className={cn("h-9 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground", cellPad, c.hide && HIDE[c.hide], c.headClassName)}>{c.srOnly ? <span className="sr-only">{c.header}</span> : c.header}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{Array.from({ length: loadingRows }).map((_, i) => <TableRow key={i} className="hover:bg-transparent">{columns.map((c, j) => <TableCell key={c.key} className={cn(cellPad, c.hide && HIDE[c.hide])}><Skeleton className="h-4 rounded" style={{ width: `${[72, 55, 64, 40, 80][(i + j) % 5]}%` }} /></TableCell>)}</TableRow>)}</TableBody>
          </Table>
        </div>
        <ul className={cn("divide-y divide-border", listBp)} aria-busy="true">{Array.from({ length: Math.min(loadingRows, 4) }).map((_, i) => <li key={i} className="space-y-2 px-4 py-3"><Skeleton className="h-4 w-2/3 rounded" /><Skeleton className="h-3 w-1/2 rounded" /></li>)}</ul>
      </>
    );
  } else if (!rows.length) {
    body = emptyState || <EmptyState compact {...(empty || { title: "Ingenting å vise" })} testId={`${testId}-empty`} />;
  } else {
    body = (
      <>
        <div className={cn("overflow-x-auto", tableBp)}>
          <Table className="min-w-[640px]">
            {caption && <caption className="sr-only">{caption}</caption>}
            <TableHeader className="sticky top-0 z-10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
              <TableRow className="hover:bg-transparent">
                {selection && <TableHead scope="col" className="h-8 w-10 px-2"><SelectBox checked={!!allSelected} indeterminate={!!someSelected} onChange={() => selection.onToggleAll(allSelected ? [] : allKeys)} label={allSelected ? "Fjern alle val" : "Vel alle"} testId={`${testId}-select-all`} /></TableHead>}
                {columns.map(headCell)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row, i) => {
                const key = rowKey(row);
                const selected = selectedKey != null && key === selectedKey;
                const checked = selSet.has(key);
                const sep = separatorBefore?.(row, i, sorted);
                return (
                  <Fragment key={key}>
                    {sep && (
                      <TableRow className="hover:bg-transparent" data-testid={`${testId}-separator`}>
                        <TableCell colSpan={columns.length + (selection ? 1 : 0)} className="px-3 py-1 sm:px-4">
                          <span className="flex items-center gap-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sol"><span className="h-px flex-1 bg-sol/60" /><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-sol" />{sep}</span><span className="h-px flex-1 bg-sol/60" /></span>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      onClick={clickable ? (e) => { if (e.target.closest("button, a, input, [role=menuitem], [data-no-row-click]")) return; onRowClick(row); } : undefined}
                      onKeyDown={keyHandler(row)}
                      tabIndex={clickable ? 0 : undefined}
                      aria-selected={selected || undefined}
                      className={cn("group/row transition-colors duration-150 hover:bg-secondary/60 focus-visible:bg-secondary/60 focus-visible:outline-none", clickable && "cursor-pointer", selected && "bg-sol/[0.09] hover:bg-sol/[0.12] shadow-[inset_3px_0_0_hsl(var(--sol))]", checked && "bg-secondary/70", rowClassName?.(row))}
                      data-testid={rowTestId?.(row) || "admin-data-table-row"}
                      data-selected={selected ? "true" : undefined}
                      data-checked={checked ? "true" : undefined}
                    >
                      {selection && <TableCell className="w-10 px-2 align-middle"><SelectBox checked={checked} onChange={() => selection.onToggle(key)} label={checked ? "Fjern val" : "Vel rad"} testId={`${testId}-select-${key}`} /></TableCell>}
                      {columns.map((c) => <TableCell key={c.key} className={cn("align-middle text-[13.5px]", cellPad, c.hide && HIDE[c.hide], c.align === "right" && "text-right tabular", c.className)}>{c.cell(row)}</TableCell>)}
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <ul className={cn("divide-y divide-border", listBp)} data-testid={`${testId}-mobile-list`}>
          {sorted.map((row, i) => {
            const key = rowKey(row);
            const checked = selSet.has(key);
            const sep = separatorBefore?.(row, i, sorted);
            return (
            <li key={key} className={cn(selectedKey != null && key === selectedKey && "bg-sol/[0.09]", checked && "bg-secondary/70", rowClassName?.(row))} data-testid={rowTestId ? `${rowTestId(row)}-mobile` : undefined}>
              {sep && <div className="flex items-center gap-3 px-4 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sol"><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-sol" />{sep}</span><span className="h-px flex-1 bg-sol/60" /></div>}
              <div
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onKeyDown={keyHandler(row)}
                className={cn("flex items-start gap-3 px-4 py-3", clickable && "cursor-pointer active:bg-secondary/60 focus-visible:bg-secondary/60 focus-visible:outline-none")}
              >
                {selection && <div className="-ml-2 shrink-0"><SelectBox checked={checked} onChange={() => selection.onToggle(key)} label={checked ? "Fjern val" : "Vel rad"} testId={`${testId}-select-${key}-mobile`} /></div>}
                {leading && <div className="shrink-0">{leading.cell(row)}</div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 text-sm font-medium">{primary?.cell(row)}</div>
                    {status && <div className="shrink-0">{status.cell(row)}</div>}
                  </div>
                  {metas.length > 0 && <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground [&_*]:text-xs">{metas.map((m) => <span key={m.key} className="min-w-0">{m.cell(row)}</span>)}</div>}
                </div>
              </div>
              {actions && <div className="-mt-1 flex justify-end gap-1 px-3 pb-2">{actions.cell(row)}</div>}
            </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <div className={cn("overflow-hidden admin-card", className)} data-testid={testId} aria-busy={loading || undefined}>
      {body}
    </div>
  );
};

/** Small helper for two-line cells (title + muted sub). */
export const Cell2 = ({ title, sub, className, titleClassName }) => (
  <div className={cn("min-w-0", className)}>
    <div className={cn("truncate", titleClassName)}>{title}</div>
    {sub ? <div className="truncate text-xs text-muted-foreground">{sub}</div> : null}
  </div>
);
